import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiClock, 
  FiDollarSign, 
  FiUsers, 
  FiPackage, 
  FiShield, 
  FiTag,
  FiCalendar,
  FiEdit
} from 'react-icons/fi';
import { getExistPlanByID } from '../../services/api/SuperAdminApi';
import { useLoading } from '../../context/LoadingContext';
import './PlanDetailsPage.scss';
// import '../UserDetailsPage/UserDetailsPage.scss';

const PlanDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();
  const [planData, setPlanData] = useState(null);

  useEffect(() => {
    if (id) {
      showLoading('Loading plan details...');
      getExistPlanByID(id)
        .then((res) => {
          setPlanData(res.data);
          hideLoading();
        })
        .catch((error) => {
          console.error('Error fetching plan details:', error);
          hideLoading();
        });
    }
  }, [id]);

  const formatPrice = (price) => {
    return price ? `₹${price.toLocaleString()}` : 'N/A';
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    
    if (duration === 1) {
      return '1 day';
    } else if (duration < 30) {
      return `${duration} days`;
    } else if (duration < 365) {
      const months = Math.floor(duration / 30);
      const remainingDays = duration % 30;
      if (remainingDays === 0) {
        return `${months} month${months > 1 ? 's' : ''}`;
      }
      return `${months} month${months > 1 ? 's' : ''} ${remainingDays} day${remainingDays > 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(duration / 365);
      const remainingDays = duration % 365;
      if (remainingDays === 0) {
        return `${years} year${years > 1 ? 's' : ''}`;
      }
      const months = Math.floor(remainingDays / 30);
      if (months === 0) {
        return `${years} year${years > 1 ? 's' : ''} ${remainingDays} day${remainingDays > 1 ? 's' : ''}`;
      }
      return `${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''}`;
    }
  };

  const formatPlanType = (type) => {
    switch (type?.toLowerCase()) {
      case 'std':
        return 'Standard';
      case 'premium':
        return 'Premium';
      case 'custom':
        return 'Custom';
      case 'ent':
        return 'Enterprise';
      case 'int':
        return 'Individual';
      default:
        return type?.toUpperCase() || 'N/A';
    }
  };

  const formatRightName = (rightKey) => {
    return rightKey
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (!planData) {
    return (
      <div className="plan-detail-container">
        <div className="error-message">Plan not found</div>
      </div>
    );
  }

  return (
    <div className="plan-detail-container">
      <div className="plan-card">
        <div className="plan-header">
          <div className="avatar">
            <FiPackage size={32} />
          </div>
          <div className="plan-title">
            <h2>{planData.name}</h2>
            <span className={`plan-status ${planData.type?.toLowerCase()}`}>
              {formatPlanType(planData.type)}
            </span>
          </div>
        </div>

        <div className="plan-details-grid">
          <div className="detail-item">
            <FiDollarSign className="icon" />
            <div>
              <label>Price</label>
              <p>{formatPrice(planData.price)}</p>
            </div>
          </div>

          <div className="detail-item">
            <FiCalendar className="icon" />
            <div>
              <label>Duration</label>
              <p>{formatDuration(planData.duration)}</p>
            </div>
          </div>

          <div className="detail-item">
            <FiPackage className="icon" />
            <div>
              <label>Cases Limit</label>
              <p>{planData.casesLimit || planData.caseLimit || 'Unlimited'}</p>
            </div>
          </div>

          <div className="detail-item">
            <FiUsers className="icon" />
            <div>
              <label>Users Limit</label>
              <p>{planData.usersLimit || planData.userLimit || 'Unlimited'}</p>
            </div>
          </div>

          <div className="detail-item">
            <FiTag className="icon" />
            <div>
              <label>Plan Type</label>
              <p className="role-badge">{formatPlanType(planData.type)}</p>
            </div>
          </div>

          <div className="detail-item">
            <FiClock className="icon" />
            <div>
              <label>Created At</label>
              <p>{planData.createdAt ? new Date(planData.createdAt).toLocaleString() : 'N/A'}</p>
            </div>
          </div>

          {planData.updatedAt && (
            <div className="detail-item">
              <FiClock className="icon" />
              <div>
                <label>Updated At</label>
                <p>{new Date(planData.updatedAt).toLocaleString()}</p>
              </div>
            </div>
          )}
        </div>

        {planData.rights && Object.keys(planData.rights).length > 0 && (
          <div className="plan-permissions-section">
            <div className="section-header">
              <div className="section-title">
                <FiShield className="icon" />
                <h3>Module Permissions</h3>
              </div>
            </div>

            <div className="permissions-grid">
              {Object.entries(planData.rights).map(([rightKey, hasPermission]) => (
                <div 
                  key={rightKey} 
                  className={`permission-item ${hasPermission ? 'enabled' : 'disabled'}`}
                >
                  <div className="permission-details">
                    <div className="permission-name">
                      {formatRightName(rightKey)}
                    </div>
                  </div>
                  <div className={`toggle-switch ${hasPermission ? 'enabled' : 'disabled'}`}>
                    <div className="toggle-slider"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="plan-actions">
          <button 
            className="edit-plan-btn"
            onClick={() => navigate(`/home/superadmin/editplan/${planData._id}`)}
          >
            <FiEdit /> Edit Plan
          </button>
          <button 
            className="back-btn"
            onClick={() => navigate('/home/superadmin')}
          >
            Back to Plans
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanDetailsPage;
